/**
 * src/services/documentosService.ts
 * =============================================================================
 * Cliente para listar y abrir documentos del workspace autenticado del
 * usuario ("Mi Espacio"). Distinto de `uploadService.ts` (que es del flujo
 * anónimo de shares) y de `documentoUploadService.ts` (que sube PDFs).
 *
 * Endpoints en el Worker:
 *   POST /me/documentos       → lista de documentos de la org activa
 *   POST /view-documento      → URL firmada GET del PDF + versionId
 *
 * Sobre paginación: hoy traemos TODOS los documentos del workspace en una
 * sola llamada. Es OK para volúmenes 0-500 docs (lo esperable para 1 Pro
 * en su primer año). Si vemos cuentas con miles, añadimos cursor.
 * =============================================================================
 */

import { api, ApiError } from '@/lib/api'

export interface DocumentoListItem {
  /** Fijado con chincheta por ESTE usuario. Los fijados salen arriba. */
  fijado?: boolean
  id: string
  nombre: string
  createdAt: string
  updatedAt: string
  /**
   * Fecha ISO en la que el cron borrará este documento, o `null` si no
   * caduca. Caducan los de cuentas free (TTL de 7 días, renovado con cada
   * versión nueva). Los de cuentas Pro y los anteriores a la migración
   * 0002 van con `null`.
   */
  expiresAt: string | null
  currentVersion: {
    id: string
    numero: number
    sizeBytes: number
    createdAt: string
  } | null
}

/** Documento compartido contigo (eres invitado, no dueño). */
export interface SharedDocumentoListItem extends DocumentoListItem {
  /** Nombre o email del usuario que te invitó, para mostrarlo en la lista. */
  inviter: string | null
}

export interface DocumentosResponse {
  documentos: DocumentoListItem[]
  compartidos: SharedDocumentoListItem[]
}

export class DocumentosError extends Error {
  code: string
  constructor(code: string, message?: string) {
    super(message ?? code)
    this.code = code
  }
}

function rebrand(e: unknown): DocumentosError {
  if (e instanceof ApiError) return new DocumentosError(e.code, e.message)
  if (e instanceof Error) return new DocumentosError('request_failed', e.message)
  return new DocumentosError('request_failed')
}

/**
 * Fija o suelta un documento. La chincheta es de cada persona, no del
 * documento: en un espacio compartido cada uno tiene su propio orden.
 */
export async function pinDocumento(documentoId: string, fijado: boolean): Promise<void> {
  await api(`/documento/${documentoId}/pin`, { method: 'POST', body: { fijado } })
}

/**
 * Devuelve los documentos del workspace activo + los compartidos contigo
 * (los que te han invitado nominalmente). Dos arrays separados — la UI
 * los pinta como secciones distintas en MisDocumentos.
 *
 * `q` filtra por nombre EN EL SERVIDOR. Es importante que sea ahí y no aquí:
 * traerse doscientas filas para esconder ciento noventa y ocho con JavaScript
 * funciona hasta que alguien tiene dos mil. El listado viene además limitado a
 * 200 documentos, así que sin buscador no habría forma de llegar al 201.
 */
export async function listDocumentos(spaceId?: string, q?: string): Promise<DocumentosResponse> {
  try {
    const busqueda = q?.trim()
    const r = await api<DocumentosResponse>('/me/documentos', {
      method: 'POST',
      body: {
        ...(spaceId ? { spaceId } : {}),
        ...(busqueda ? { q: busqueda } : {}),
      },
    })
    return {
      documentos: r.documentos ?? [],
      compartidos: r.compartidos ?? [],
    }
  } catch (e) {
    throw rebrand(e)
  }
}

/**
 * Renombra un documento. El nuevo nombre se trunca a 120 chars y se
 * sanea trim() en el servidor — el frontend pasa lo que tenga.
 * Devuelve el nombre efectivo aplicado (puede diferir si se truncó).
 */
export async function renameDocumento(documentoId: string, nombre: string): Promise<string> {
  try {
    const r = await api<{ ok: boolean; nombre: string }>('/rename-documento', {
      method: 'POST',
      body: { documentoId, nombre },
    })
    return r.nombre
  } catch (e) {
    throw rebrand(e)
  }
}

/**
 * Borra el documento + cascade en D1 (versiones, anotaciones) + R2.
 * Operación irreversible — el caller debe pedir confirmación al user
 * antes de llamar.
 */
export async function deleteDocumento(documentoId: string): Promise<void> {
  try {
    await api('/delete-documento', {
      method: 'POST',
      body: { documentoId },
    })
  } catch (e) {
    throw rebrand(e)
  }
}

// ---------------------------------------------------------------------------
// Invitaciones nominales (Bloque 3).
// ---------------------------------------------------------------------------

export interface InviteInfo {
  invitedEmail: string
  documentoNombre: string
  inviterName: string
  status: 'pending' | 'accepted' | 'revoked'
  expired: boolean
}

export interface InviteResult {
  email: string
  /** URL completa del magic link. La devolvemos siempre para que el user
   *  pueda copiarla y mandarla por otro canal si el email no llega. */
  acceptUrl: string
  /** true si Resend entregó el envío al SMTP. false si Resend falló (o
   *  no hay api key). El caller debe mostrar UI distinta en cada caso. */
  emailSent: boolean
  /** Si emailSent=false, mensaje técnico de por qué (para logs/devs). */
  emailError: string | null
}

/**
 * Invita a un email a anotar un documento. Solo Pro. El backend crea
 * una row pendiente + intenta mandar magic link al email. Idempotente:
 * si ya hay una invitación pending al mismo email para el mismo doc,
 * reusa el token y reintenta el email.
 *
 * Devuelve siempre el `acceptUrl` aunque el email haya fallado — el
 * frontend lo muestra al usuario para que pueda copiarlo manualmente.
 */
export async function inviteToDocumento(
  documentoId: string,
  email: string,
): Promise<InviteResult> {
  try {
    return await api<InviteResult>(`/documento/${documentoId}/invite`, {
      method: 'POST',
      body: { email },
    })
  } catch (e) {
    throw rebrand(e)
  }
}

/**
 * Devuelve los datos públicos asociados a un token de invitación, para
 * que la página de signup express muestre "Roberto te invita a A01.pdf"
 * antes de que el invitado meta password.
 */
export async function getInviteInfo(token: string): Promise<InviteInfo> {
  // Endpoint GET con token en query string. Reutilizamos api() haciendo
  // POST no aplica; usamos fetch directo. La api lib expone apiUrl.
  const url = `/invite-info?token=${encodeURIComponent(token)}`
  try {
    return await api<InviteInfo>(url, { method: 'GET' })
  } catch (e) {
    throw rebrand(e)
  }
}

/**
 * Acepta una invitación. Marca la row como accepted con accepted_by_user_id
 * = current user. Devuelve el documentoId al que se ha concedido acceso
 * para que el cliente navegue a /d/<id>.
 */
export async function acceptInvite(token: string): Promise<string> {
  try {
    const r = await api<{ documentoId: string }>('/accept-invite', {
      method: 'POST',
      body: { token },
    })
    return r.documentoId
  } catch (e) {
    throw rebrand(e)
  }
}

/**
 * Una invitación tal como la lista el endpoint /documento/:id/invitations.
 * Pending = el invitado todavía no clicó el magic link.
 * Accepted = el invitado entró y ya tiene acceso (la fila lleva accepted_by_user_id).
 * Revoked NO viene en esta lista (el endpoint la filtra).
 */
export interface InvitationRow {
  id: string
  email: string
  status: 'pending' | 'accepted'
  createdAt: string
  acceptedAt: string | null
  /** full_name del aceptante, si lo tenemos (puede ser null si aceptó sin
   *  perfil completo o si todavía es pending). */
  acceptedName: string | null
}

/**
 * Lista las invitaciones pending + accepted de un documento. Solo el
 * dueño (membership directa) puede llamar esto. La UI lo usa en el
 * ShareModal para pintar "Personas con acceso".
 */
export async function listInvitations(documentoId: string): Promise<InvitationRow[]> {
  try {
    const r = await api<{ invitations: InvitationRow[] }>(
      `/documento/${documentoId}/invitations`,
      { method: 'GET' },
    )
    return r.invitations ?? []
  } catch (e) {
    throw rebrand(e)
  }
}

/**
 * Revoca una invitación — pending o accepted. Si era accepted, el
 * aceptante pierde acceso al doc en la siguiente request (el middleware
 * filtra por status='accepted').
 */
export async function revokeInvitation(invitationId: string): Promise<void> {
  try {
    await api('/revoke-invitation', {
      method: 'POST',
      body: { invitationId },
    })
  } catch (e) {
    throw rebrand(e)
  }
}
